import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Dropdown, Select, Image, Input, Radio, RadioChangeEvent, Checkbox, Tooltip } from 'antd';
import { GetProductProjectsReponse, ProductReferences } from '../../../../../redux/actions/productActions/types';
import { blueDark01, blueLink, grayDark05, grayDark03 } from '../../../../../styles/colors';
import { PrimaryButton, BoldText, Text, Tag, PrimarySmallButton } from '../../../../../styles/common.styles';
import WarningBanner from '../../../../../components/warningBanner/WarningBanner';
import {
    ActionCloseContainer,
    ActionContainer,
    ActionItem,
    ActionsSection,
    BrandSection,
    DateSection,
    ProductItemPropertiesLeftSection,
    PricingError,
    FinishScheduleIdError,
    PricingInputContainer,
    FinishScheduleIdInputContainer,
    ProductItemPropertiesRightSection,
    ProductContainer,
    ProductItemDivider,
    ProductItemLabel,
    ProductNameSection,
    ProductRadioButtonContainer,
    DetailItemsSection,
    ProductDetailsWrapper,
    QuintileInformation,
    DescriptionInputContainer,
    FavoriteButton,
    DiscussAndUploadAndCounts,
    TextCounts,
    TextCountsItem,
    ProgramRow,
    TipContainer,
    TipContainerImpactAreas,
    ColorCircle,
    ResultTextAfterCircle,
    PercentageText,
    DotsAction,
    OpenMenuButton,
    ProductActionMenuItem,
    EPDBlock,
    MaterialProgramBlock,
    TagsList,
    LiveGuidelineTag,
    LiveGuidelineTagIcon,
    SectionDivider,
    RowFlex,
    NoTagsText
} from './ProductItem.styles';
import ProgramTipsController from '../../../../../components/favoritProducts/favoriteProductItem/productItemHeader/tips/materialPrograms/ProgramTipsController';
import {
    formatRegulationText,
    getColorCircleForResult,
    getPercentileColor,
} from '../../../../../components/favoritProducts/favoriteProductItem/productItemHeader/ProductItemHeaderHelper';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { BrandCompany, FavoriteProduct, InternalProduct, Product } from '../../../../../models/product/types';
import {
    changeBasisOfDesignAction,
    changeFinishScheduleIdAction,
    changePricingTypeAction,
    changeSubstitutionAction,
    deleteProductAction,
    switchProductInstalledAction,
    updateDescriptionAction,
    updatePricingAction,
    copyProductAction,
    getAllProductProjectsAction,
} from '../../../../../redux/actions/productActions';
import {
    addToFavoriteAction,
    removeFromFavoriteAction,
} from '../../../../../redux/actions/favoriteProductsActions';
import { toShortLocalDate } from '../../../../../helpers/dateHelper';
import { alertService } from '../../../../../services';
import { useModalState } from '../../../../../hooks/modalState';
import DeleteModal from '../../../../../components/common/deleteModal/DeleteModal';
import { useProjectProductTags } from '../projectProductTagsContext/ProjectProductTagsContext';
import { ActionResponse, CommonResponse } from '../../../../../models/actionResponse/types';
import { EC3EmbodiedCarbon, FavoriteType, MasterFormatBackground } from '../../../../../models/product/enums';
import HelpModal from '../../../../../components/helpModal/HelpModal';
import { usePermissions } from '../../../../../hooks/permissions';
import { SubscriptionTypes } from '../../../../../models/subscription/enums';
import { AccountPermissionTypes, ProjectPermissionTypes } from '../../../../../models/permission/enums';
import { GetProjectInformationResponse } from '../../../../../redux/actions/projectActions/types';
import {
    EC3Rect,
} from '../../../../../components/common/ec3/EC3Rect.styles';
import ProductEPDSection from './productEPDSection/ProductEPDSection';
import ActiveOfficeFavoriteIcon from '../../../../../components/common/activeOfficeFavoriteIcon/ActiveOfficeFavoriteIcon';
import OfficePromptModal from '../../../../../components/common/officeFavoritePromptModal/OfficePromptModal';
import MyPromptModal from '../../../../../components/common/myFavoritePromptModal/MyPromptModal';
import BrandInnerData from './BrandInnerData';
import { ProjectProductTag } from '../../../../../redux/reducers/projectProductTagReducer/ProjectProductTagSlice';
import { splitAndSortCommonProductTags, truncateLiveGuidelineTagName } from '../../../../../utils/tagUtils';
import { deleteManualProductInTc } from '../../../../../helpers/deleteManuallyAddedProductHelper';
import { tryRefreshSession } from '../../../../../helpers/request';
import { getAccessToken, getRefreshToken, setCookies } from '../../../../../redux/persistedState';
import { buildEditManualProductInTcUrl } from '../../../../../helpers/editManullyAddedProductHelper';

const { Option } = Select;
const EMPTY_PRODUCT_TAGS: ProjectProductTag[] = [];

interface Props {
    product: Product;
    references: ProductReferences | null;
    projectInfo: GetProjectInformationResponse | null;
    previousProduct?: Product;
    installedTab?: boolean;
    editable: boolean;
    divider: boolean;
    brandInfo?: BrandCompany;
    lastProduct: boolean;
    filterModalOpened?: boolean;
    previousProductManuallyAdded?: boolean;
    manuallyAddedProductsByBrand: Product[];
    updateDivisions: (product: Product, isInstalled?: boolean) => void;
    handleDeleteProduct: (product: Product) => void;
}

export const getProductActions = (isProductManuallyAdded = false) => {
    return [
        ...(isProductManuallyAdded ? [{
            key: "EditProduct",
            label:(
                <ProductActionMenuItem>Edit</ProductActionMenuItem>
            )
        }] : []),
        {
            key: "CopyProduct",
            label: (
                <ProductActionMenuItem>Add another</ProductActionMenuItem>
            ),
        },
        { type: 'divider' },
        {
            key: "Delete",
            label: (
                <ProductActionMenuItem>Delete</ProductActionMenuItem>
            ),
        },
    ];
};

export const ProductRowActions: React.FC<Props> = ({ handleActions, project, product, disabled }) => {
    const [open, setOpen] = React.useState(false);
    const submitAction = (data: { key: string }) => {
        handleActions(data.key, project, product);
    };
    const handleMenuOpen = () => {
        setOpen(!open);
    };
    return (
        <Dropdown
            menu={{
                items: getProductActions(product?.type === "userProducts"),
                onClick: submitAction,
            }}
            overlayClassName="projects-table-columns-dropdown"
            placement="bottom"
            trigger={['click']}
            onOpenChange={handleMenuOpen}
            disabled={disabled}
        >
            <div>
                <OpenMenuButton>
                    <DotsAction></DotsAction>
                </OpenMenuButton>
            </div>
        </Dropdown>
    );
};

const ProductItem: React.FC<Props> = ({
    product,
    projectInfo,
    references,
    previousProduct,
    installedTab,
    editable,
    divider,
    brandInfo,
    lastProduct,
    updateDivisions,
    handleDeleteProduct,
    filterModalOpened,
    previousProductManuallyAdded,
    manuallyAddedProductsByBrand
}) => {
    const dispatch = useAppDispatch();
    const authReducer = useAppSelector((s) => s.authReducer);
    const productPending = useAppSelector((s) => s.productReducer.pending);
    const history = useHistory();

    const [state, setState] = useState({ current: product });
    const [errors, setErrors] = useState({
        pricing: false,
        finishScheduleId: false,
    });
    const { onOpen, onClose, isOpen } = useModalState();
    const { onOpen: openHelp, onClose: closeHelp, isOpen: isHelpOpen } = useModalState();
    const { getAccountRole, getAccountSubscription, canAddToOrgFavorites, canManageTags, getProjectPermission } = usePermissions();
    const promptModal = useModalState();
    const myPromptModal = useModalState();
    const { open: openProjectProductTags, deleteTag: deleteProjectProductTag } = useProjectProductTags();

    const isCommentorOnly = getProjectPermission([ProjectPermissionTypes.COMMENTER], projectInfo?.userRole);
    const userCanEditProject = getProjectPermission(
        [ProjectPermissionTypes.EDITOR, ProjectPermissionTypes.OWNER, ProjectPermissionTypes.PROJECT_ADMIN],
        projectInfo?.userRole,
    );
    const isMfrTemplateViewOnly = projectInfo?.isFromManufacturer === true && !userCanEditProject;
    const canManageTag = (
        getAccountRole() === AccountPermissionTypes.ACCOUNT_ADMIN
        || (getAccountRole() === AccountPermissionTypes.ACCOUNT_USER && canManageTags())
    ) && !isCommentorOnly && !isMfrTemplateViewOnly;

    const projectProductIdForTags = state?.current?.projectProductId ?? null;

    const rawProjectProductTags = useAppSelector((s) => {
        const byProjectProduct = (s as any).projectProductTagReducer?.byProjectProductId ?? {};
        return projectProductIdForTags ? byProjectProduct[projectProductIdForTags]?.tags : undefined;
    });

    const projectProductTags = useMemo(
        () => splitAndSortCommonProductTags(rawProjectProductTags ?? EMPTY_PRODUCT_TAGS),
        [rawProjectProductTags],
    );

    const disabledImgClassName = 'disabled-img';

    const mfrManuallyAdded = brandInfo?.type === 'userCompanies';
    const productManuallyAdded = product.type === 'userProducts' && brandInfo?.type === 'companies';
    const isManuallyAdded = productManuallyAdded || mfrManuallyAdded;

    useEffect(() => {
        if (product) {
            setState({ ...state, current: product });
        }
    }, [product]);

    const openAddProjectProductTag = useCallback(() => {
        if (!projectProductIdForTags) {
            alertService.error('Cannot determine project product id for tags');
            return;
        }
        openProjectProductTags(projectProductIdForTags);
    }, [projectProductIdForTags, openProjectProductTags]);
    const handleMyFavorite = async () => {
        if (productPending || getAccountSubscription() === SubscriptionTypes.FREE) {
            return;
        }
        let result;
        if (!product.isMyFavorite) {
            const { payload } = (await dispatch(
                addToFavoriteAction({
                    productId: product.productId,
                    projectId: product.projectId,
                    favoriteType: FavoriteType.MyFavorite,
                }),
            )) as ActionResponse<FavoriteProduct>;
            if (payload.data) {
                product.myFavoriteId = payload.data.id;
                myPromptModal.onToggle();
            }
            result = payload;
        } else {
            if (product.myFavoriteId) {
                const { payload } = (await dispatch(
                    removeFromFavoriteAction({
                        favoriteType: FavoriteType.MyFavorite,
                        productFavoriteId: product.myFavoriteId,
                    }),
                )) as CommonResponse;
                result = payload;
            }
        }
        if (result) {
            updateDivisions({ ...product, isMyFavorite: !product.isMyFavorite });
        }
    };
    const handleOfficeFavorite = async () => {
        if (productPending) {
            return;
        }
        let result;
        if (!product.isOfficeFavorite) {
            const { payload } = (await dispatch(
                addToFavoriteAction({
                    productId: product.productId,
                    projectId: product.projectId,
                    favoriteType: FavoriteType.OfficeFavorite,
                }),
            )) as ActionResponse<FavoriteProduct>;
            if (payload.data) {
                product.officeFavoriteId = payload.data.id;
                promptModal.onToggle();
            }
            result = payload;
        } else {
            if (product.officeFavoriteId) {
                const { payload } = (await dispatch(
                    removeFromFavoriteAction({
                        favoriteType: FavoriteType.OfficeFavorite,
                        productFavoriteId: product.officeFavoriteId,
                    }),
                )) as CommonResponse;
                result = payload;
            }
        }
        if (result) {
            updateDivisions({ ...product, isOfficeFavorite: !product.isOfficeFavorite });
        }
    };

    const handleBasisOfDesign = async (basisOfDesignId: number) => {
        const currentProduct = state.current;
        currentProduct.basisOfDesignId = basisOfDesignId;
        setState({ ...state, current: currentProduct });
        await dispatch(
            changeBasisOfDesignAction({
                basisOfDesignId,
                productId: product.productId,
                projectId: product.projectId,
                projectProductId: product.projectProductId,
            }),
        );
        updateDivisions(currentProduct);
    };

    const handleFinishScheduleId = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const finishScheduleId = event.target.value;
        if (finishScheduleId.length > 50) {
            setErrors({ ...errors, finishScheduleId: true });
            setTimeout(() => {
                setErrors({ ...errors, finishScheduleId: false });
            }, 5000);
            return;
        }
        const currentProduct = state.current;
        currentProduct.finishScheduleId = finishScheduleId;
        setState({ ...state, current: currentProduct });
    };

    const handleSubstitution = async (substitutionTypeId: number) => {
        const currentProduct = state.current;
        currentProduct.substitutionTypeId = substitutionTypeId;
        setState({ ...state, current: currentProduct });
        await dispatch(
            changeSubstitutionAction({
                substitutionsId: substitutionTypeId,
                productId: product.productId,
                projectId: product.projectId,
                projectProductId: product.projectProductId,
            }),
        );
        updateDivisions(currentProduct);
    };

    const handleInstalledProduct = async () => {
        const currentProduct = state.current;
        currentProduct.isInstalled = !currentProduct.isInstalled;
        setState({ ...state, current: currentProduct });
        await dispatch(
            switchProductInstalledAction({
                productId: product.productId,
                projectId: product.projectId,
                projectProductId: product.projectProductId,
            }),
        );
        updateDivisions(currentProduct, true);
    };

    const handlePricingType = async (event: RadioChangeEvent) => {
        const pricingTypeId = event.target.value || ' ';
        if (pricingTypeId) {
            const currentProduct = state.current;
            currentProduct.pricingTypeId = pricingTypeId;
            setState({ ...state, current: currentProduct });
            await dispatch(
                changePricingTypeAction({
                    pricingTypeId,
                    productId: product.productId,
                    projectId: product.projectId,
                    projectProductId: product.projectProductId,
                }),
            );
            updateDivisions(currentProduct);
        } else {
            alertService.error('Something went wrong');
        }
    };

    const handlePricing = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const pricing = event.target.value;
        if (pricing.length > 75) {
            setErrors({ ...errors, pricing: true });
            setTimeout(() => {
                setErrors({ ...errors, pricing: false });
            }, 5000);
            return;
        }
        const currentProduct = state.current;
        currentProduct.pricing = pricing;
        setState({ ...state, current: currentProduct });
    };

    const savePricing = async () => {
        const currentProduct = state.current;
        await dispatch(
            updatePricingAction({
                pricing: currentProduct.pricing || '',
                productId: product.productId,
                projectId: product.projectId,
                projectProductId: product.projectProductId,
            }),
        );
        updateDivisions(currentProduct);
    };

    const saveFinishScheduleId = async () => {
        const currentProduct = state.current;
        await dispatch(
            changeFinishScheduleIdAction({
                finishScheduleId: currentProduct.finishScheduleId || '',
                productId: product.productId,
                projectId: product.projectId,
                projectProductId: product.projectProductId,
            }),
        );
        updateDivisions(currentProduct);
    };

    const handleDescription = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const description = event.target.value;
        const currentProduct = state.current;
        currentProduct.description = description;
        setState({ ...state, current: currentProduct });
    };

    const handleDescriptionBlur = (event: any) => {
        event.target.scrollLeft = 0;
        saveDescription();
    }

    const saveDescription = async () => {
        const currentProduct = state.current;
        await dispatch(
            updateDescriptionAction({
                description: currentProduct.description || '',
                productId: product.productId,
                projectId: product.projectId,
                projectProductId: product.projectProductId,
            }),
        );
        updateDivisions(currentProduct);
    };

    const deleteProduct = async () => {
    try {
        const userId = authReducer?.user?.id;

        if (isManuallyAdded) {
            if (!userId) {
                alertService.error('Could not determine current user');
                return;
            }
            const tcResult = await deleteManualProductInTc({
                project: product.projectId,
                userId,
                productId: product.productId,
            });
            if (!tcResult.success) {
                alertService.error(tcResult.message || 'Failed to delete product from catalog');
                return;
            }
            if(product.myFavoriteId){

                const { payload } = (await dispatch(
                    removeFromFavoriteAction({
                        favoriteType: FavoriteType.MyFavorite,
                        productFavoriteId: product.myFavoriteId,
                    }),
                )) as CommonResponse;
                if(payload){
                    updateDivisions({ ...product, isMyFavorite: !product.isMyFavorite });
                }
            }
            if(product.officeFavoriteId){

                const { payload } = (await dispatch(
                    removeFromFavoriteAction({
                        favoriteType: FavoriteType.OfficeFavorite,
                        productFavoriteId: product.officeFavoriteId,
                    }),
                )) as CommonResponse;
                if(payload){
                    updateDivisions({ ...product, isMyFavorite: !product.isMyFavorite });
                }
            }

            const {payload: response} = await dispatch(getAllProductProjectsAction(product.productId)) as ActionResponse<GetProductProjectsReponse[]>
            if(response?.data){
                for(const productProject of response.data){
                    const { payload } = await dispatch(
                        deleteProductAction({
                            productId: productProject.productId,
                            projectId: productProject.projectId,
                            projectProductId: productProject.projectProductId ,
                        }),
                    );
                    if (payload) {
                        handleDeleteProduct(product);
                    }
                }
            }

            return;
        }

        const { payload } = await dispatch(
            deleteProductAction({ productId: product.productId, projectId: product.projectId, projectProductId: product.projectProductId }),
        );
        if (payload) {
            handleDeleteProduct(product);
        }

    } catch (error: any) {
        alertService.error(error?.message || 'Failed to delete product');
    }};

    const goToMFRPage = async (mode: string) => {
        window.history.replaceState(
            null,
            '',
            `/project-info/${product.product.projectId}/0/${installedTab ? '3' : '2'}?productId=${product.productId
            }`,
        );
        const redir = `/product-info/${product.product.projectId}/${product.productId}/` + `-1/` + (product.product.id ? `${product.product.id}` : `-1`) + (mode ? `/${mode}` : ``);
        history.push(redir);
    };

    const goToMFRPageDefault = async () => {
        goToMFRPage('');
    };

    const goToMFRPageComments = async () => {
        goToMFRPage('comments');
    };

    const goToMFRPageDocuments = async () => {
        goToMFRPage('documents');
    };

    const goToMFRPageImages = async () => {
        goToMFRPage('images');
    };

    const getFreeListingClassName = () => {
        if (!brandInfo) {
            return '';
        }

        return brandInfo.level === MasterFormatBackground.FREE_LISTING ? 'freeListing' : '';
    }

    let sameBrand = product.companyID === previousProduct?.companyID;
    if (product.companyID === undefined || product.companyID === null) {
        sameBrand = product.company === previousProduct?.company;
    }

    const productIsNotIncludedWarningText = "Product no longer included in the MFR's Transparency Catalog listing.";

    const notAllowedClassName = 'not-allowed';

    const handleActions = async (action, project, selectedProduct) => {
        if (action === 'CopyProduct') {
            const result = (await dispatch(
                copyProductAction({
                    projectProductId: selectedProduct.projectProductId,
                    projectId: selectedProduct.projectId,
                    productId: selectedProduct.productId,
                }),
            )) as ActionResponse<InternalProduct[]>;
            if (result?.payload?.data) {
                const redir = `/project-info/${product.product.projectId}/0/2?productId=${product.productId ? product.productId : -1}`;
                history.push(redir);
                window.location.href = redir;
            }
        }
        else if (action === 'Delete') {
            onOpen();
        }
        else if ( action === 'EditProduct') {
            const projectId = projectInfo?.id ?? selectedProduct.projectId;
            const userId = authReducer?.user?.id;
            const organizationId = authReducer?.user?.organization?.id;
            const tcProductId = selectedProduct?.productId;

            if (!projectId || !userId || !organizationId || !tcProductId) {
                alertService.error('Something went wrong');
                return;
            }

            await tryRefreshSession();
            setCookies(getAccessToken(), getRefreshToken());

            // Single navigation — same shape as "Add product not in Transparency Catalog" + productId (no JSON preflight).
            const editUrl = buildEditManualProductInTcUrl({
                projectId,
                userId,
                organizationId,
                productId: tcProductId,
                returnUrl: window.location.href,
            });

            window.location.href = editUrl;
        }
    }

    const getHpdResultsString = (product: Product) => {
        const hpdResults: string[] = [];

        if (product.perkinsWillPrecautionaryListFree) {
            hpdResults.push("Perkins&Will PL Free");
        }
        if (product.livingBuildingChallengeRedListFree) {
            hpdResults.push("LBC Red List Free");
        }
        if (product.c2cRestrictedListFree) {
            hpdResults.push("C2C Restricted List Free");
        }

        const hpdResultsJSX = hpdResults.map((result, index) => (
            <span key={index} className="results hpd">{index < hpdResults.length - 1 ? [result, ', '] : result}</span>
        ));

        return hpdResultsJSX.length > 0 ? <>{hpdResultsJSX}</> : null;
    };

    const getLowEmittingInfo = (product: Product) => {
        const verifications = [
            { verified: product.hasCleanAirSilver, url: product.cleanAirSilverUrl, label: 'Clean Air Silver' },
            { verified: product.hasCleanAirGold, url: product.cleanAirGoldUrl, label: 'Clean Air Gold' },
            { verified: product.hasClearchem, url: product.clearchemUrl, label: 'ClearChem™' },
            { verified: product.hasFloorscore, url: product.floorscoreUrl, label: 'FloorScore®' },
            { verified: product.hasGreenCertified, url: product.greenCertifiedUrl, label: 'Green Certified®' },
            { verified: product.hasGreenguard, url: product.greenguardUrl, label: 'GREENGUARD' },
            { verified: product.hasGreenguardGold, url: product.greenguardGoldUrl, label: 'GREENGUARD Gold' },
            { verified: product.hasIndoorAdvantage, url: product.indoorAdvantageUrl, label: 'Indoor Advantage™' },
            { verified: product.hasIndoorAdvantageGold, url: product.indoorAdvantageGoldUrl, label: 'Indoor Advantage Gold™' },
        ];

        const parts = verifications
            .filter(v => v.verified)
            .map((v, index) => <a key={index} href={v.url} target="_blank" rel="noopener noreferrer">{v.label}</a>)

        return parts.length > 0 ? <>{parts.map((part, i) => i > 0 ? [', ', part] : part)}</> : null;
    };

    const getEPDProgramLink = (product: Product) => {
        if (product.isOptimizedEpd && !product.epdProgram.toLowerCase().includes('sminds')) {
            const epdOptimizedText = ' Optimized'
            let epdProgramText = product.epdProgram;
            epdProgramText = epdProgramText.replace('+Opt', '');

            return (
                <>
                    <a href={product.epdUrl} target="_blank" rel="noopener noreferrer">{epdProgramText}</a>
                    <a href={product.optimizedEpdUrl} target="_blank" rel="noopener noreferrer">{epdOptimizedText}</a>
                </>
            );
        }

        return (
            <a href={product.epdUrl} target="_blank" rel="noopener noreferrer">
                {(product.epdType.toLowerCase() === 'industry average' ? 'IA ' : '') + product.epdProgram}
            </a>
        )
    }

    const hasEPDOrEC3Data = (product.epdProgram || (product.ec3Url || product.ec3EmbodiedCarbon))
    const hasMaterialDisclosuresData = product.declare || product.lpc || product.c2cMHC ||
        (product.hpdScopeResults || product.hpdUrl) || product.c2cCertified || product.mfrInv;

    return (
        <ProductContainer
            className={`product-item-container ${filterModalOpened ? 'shifted' : ''}`}
            id={product.product.productId.toString()}
        >
            <BrandSection className={`product-item${mfrManuallyAdded ? '-gray' : ''} ${divider ? 'withBorder' : ''} ${getFreeListingClassName()}`}>
                {
                    (!sameBrand || productManuallyAdded) && !(previousProductManuallyAdded && sameBrand)
                    && (
                        <BrandInnerData
                            product={product}
                            brandInfo={brandInfo}
                            sameBrand={sameBrand}
                            productManuallyAdded={productManuallyAdded}
                            mfrManuallyAdded={mfrManuallyAdded}
                            openHelp={openHelp}
                            manuallyAddedProductsByBrand={manuallyAddedProductsByBrand}
                        />
                    )}
            </BrandSection>
            <ProductDetailsWrapper>
                {(product.status !== 'live' && !isManuallyAdded) && (
                    <WarningBanner Text={productIsNotIncludedWarningText}></WarningBanner>
                )}
                <DetailItemsSection className={`${filterModalOpened ? 'shifted' : ''}`}>
                    <ProductNameSection
                        className={`product-item`}
                    >
                        {product.status === 'live' && product.productUrl && product.productUrl.trim() ? (
                            <a href={product.productUrl} target="_blank">
                                <BoldText weight={400} size={14} height={18} color={blueLink}>
                                    {product.productName}
                                </BoldText>
                            </a>
                        ) : (
                            <BoldText weight={400} size={14} height={18} color={blueDark01}>
                                {product.productName}
                            </BoldText>
                        )}
                        {product.product.storage === 'TC' && (
                            <ProductEPDSection product={product} epdProgram={'IA ' + product.epdProgram} hpd={product.hpd} />
                        )}

                        {product.product.storage === 'TC' && (
                            <QuintileInformation>
                                {' '}
                                {hasEPDOrEC3Data && (
                                    <EPDBlock>
                                        {product.epdProgram && (
                                            <ProgramRow>
                                                <div className="program-spacing">
                                                    <div className="program">
                                                        {' '}
                                                        {product.epdProgram && (

                                                            <>
                                                                {getEPDProgramLink(product)}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <TipContainer className="ec3">
                                                    <ProgramTipsController
                                                        materialProgram='EpProgram'
                                                        productsTab={true}
                                                    />
                                                </TipContainer>
                                            </ProgramRow>
                                        )}

                                        {(product.ec3Url || product.ec3EmbodiedCarbon) && (
                                            <ProgramRow>
                                                <div className="program-spacing">
                                                    <div className="program">
                                                        {' '}
                                                        {product.ec3Url && (
                                                            <>
                                                                <a href={product.ec3Url} target="_blank">
                                                                    {`CO2e | EC3${product.ec3EmbodiedCarbon || product.ec3CategoryStatus ? ':' : ''}`}
                                                                </a>

                                                            </>
                                                        )}
                                                    </div>
                                                    {product.ec3EmbodiedCarbon && (
                                                        <div className="program">
                                                            {' '}
                                                            <>
                                                                <EC3Rect
                                                                    className="ec3Rect"
                                                                    color={getPercentileColor(
                                                                        product.ec3EmbodiedCarbon as EC3EmbodiedCarbon,
                                                                    )}
                                                                />
                                                                <PercentageText>{`${product.ec3EmbodiedCarbon}${product.ec3CategoryStatus ? ',' : ''}`}</PercentageText>
                                                            </>
                                                        </div>
                                                    )}
                                                    {product.ec3CategoryStatus && (
                                                        <div className="program">
                                                            {' '}
                                                            <>
                                                                <PercentageText>{product.ec3CategoryStatus}</PercentageText>
                                                            </>
                                                        </div>
                                                    )}
                                                </div>
                                                <TipContainer className="ec3">
                                                    <ProgramTipsController
                                                        materialProgram='ListingEc3'
                                                    />
                                                </TipContainer>
                                            </ProgramRow>
                                        )}
                                    </EPDBlock>
                                )}
                                {hasMaterialDisclosuresData && (
                                    <MaterialProgramBlock>
                                        {product.declare && (
                                            <ProgramRow>
                                                <div className="program-spacing">
                                                    <div className="program">
                                                        {' '}
                                                        {product.declare && (
                                                            <>
                                                                <a href={product.declareUrl} target="_blank" rel="noopener noreferrer">
                                                                    {`${product.declare}${product.declareResults ? ':' : ''}`}
                                                                </a>

                                                            </>
                                                        )}
                                                    </div>
                                                    {product.declareResults && (
                                                        <div className="results circle">
                                                            <ColorCircle
                                                                color={getColorCircleForResult(
                                                                    product.declareResults as DeclareResults,
                                                                )}
                                                            />
                                                            <ResultTextAfterCircle>{product.declareResults}</ResultTextAfterCircle>
                                                        </div>
                                                    )}
                                                </div>
                                                <TipContainer className="ec3">
                                                    <ProgramTipsController
                                                        materialProgram={product.declare}
                                                    />
                                                </TipContainer>
                                            </ProgramRow>
                                        )}

                                        {product.lpc &&
                                            (
                                                <ProgramRow>
                                                    <div className="program-spacing">
                                                        <div className="program">
                                                            {' '}
                                                            {product.lpc && (
                                                                <>
                                                                    <a href={product.lpcUrl} target="_blank" rel="noopener noreferrer">
                                                                        {`${product.lpc}${product.lpcResults ? ':' : ''}`}
                                                                    </a>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="results">
                                                            {product.lpcResults}
                                                        </div>
                                                    </div>
                                                    <TipContainer className="ec3">
                                                        <ProgramTipsController
                                                            materialProgram={product.lpc}
                                                        />
                                                    </TipContainer>
                                                </ProgramRow>
                                            )}
                                        {product.c2cMHC && (
                                            <ProgramRow className={product.hpd || product.declare ? 'ec3' : ''}>
                                                <div className="program-spacing">
                                                    <div className="program">
                                                        {' '}
                                                        {product.c2cMHC && (
                                                            <>
                                                                <a href={product.c2cMHCUrl} target="_blank" rel="noopener noreferrer">
                                                                    {`${product.c2cMHC}${product.c2cMHCResults ? ':' : ''}`}
                                                                </a>

                                                            </>
                                                        )}
                                                    </div>
                                                    {product.c2cMHCResults && (
                                                        <div className="results circle">
                                                            <ColorCircle
                                                                color={getColorCircleForResult(
                                                                    product.c2cMHCResults as DeclareResults,
                                                                )}
                                                            />
                                                            {product.c2cMHCResults}
                                                        </div>
                                                    )}
                                                </div>
                                                <TipContainer className="ec3">
                                                    <ProgramTipsController
                                                        materialProgram={product.c2cMHC}
                                                    />
                                                </TipContainer>
                                            </ProgramRow>
                                        )}

                                        {((product.hpdScopeResults || product.hpdUrl) && (
                                            <>
                                                <ProgramRow
                                                    className={
                                                        product.hpd || product.declare || product.c2cMHC
                                                            ? 'ec3'
                                                            : ''
                                                    }
                                                >
                                                    <div className="program-spacing-hpd">
                                                        <div className={`program ${getHpdResultsString(product) ? 'has-hpd-results' : ''}`}>
                                                            {' '}
                                                            {product.hpd && (
                                                                <>
                                                                    <a href={product.hpdUrl} target="_blank" rel="noopener noreferrer">
                                                                        {`${product.hpd}${getHpdResultsString(product) ? ':' : ''}`}
                                                                    </a>
                                                                </>
                                                            )}
                                                        </div>
                                                        {getHpdResultsString(product)}
                                                        <TipContainer className="ec3">
                                                            <ProgramTipsController
                                                                materialProgram={product.hpd}
                                                            />
                                                        </TipContainer>
                                                    </div>

                                                </ProgramRow>
                                            </>
                                        ))}

                                        {((product.c2cCertified) && (
                                            <ProgramRow
                                                className={
                                                    product.hpd || product.declare || product.c2cMHC
                                                        ? 'ec3'
                                                        : ''
                                                }
                                            >
                                                <div className="program-spacing">
                                                    <div className="program low-emitting">
                                                        {' '}
                                                        {product.c2cCertified && (
                                                            <>
                                                                <a href={product.c2cUrl} target="_blank" rel="noopener noreferrer">
                                                                    {`${product.c2cCertified}${product.c2cResults || product.regulations ? ':' : ''}`}
                                                                </a>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="results circle">
                                                        {product.c2cResults && (
                                                            <>
                                                                <ColorCircle
                                                                    color={getColorCircleForResult(
                                                                        product.c2cResults as DeclareResults,
                                                                    )}
                                                                />
                                                                {product.c2cResults}
                                                            </>
                                                        )}
                                                        {product.regulations && (
                                                            <div>
                                                                {product.regulations.map((r) => (
                                                                    <div>{formatRegulationText(r)}</div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <TipContainer className="ec3">
                                                    <ProgramTipsController
                                                        materialProgram={product.c2cCertified}
                                                    />
                                                </TipContainer>
                                            </ProgramRow>
                                        ))}
                                        {product.mfrInv && (
                                            <ProgramRow
                                                className={
                                                    product.hpd || product.c2cCertified || product.c2cMHC
                                                        ? 'ec3'
                                                        : ''
                                                }
                                            >
                                                <div className="program-spacing">
                                                    <div className="program">
                                                        {' '}
                                                        {product.mfrInv && (
                                                            <>
                                                                <a href={product.mfrInvUrl} target="_blank" rel="noopener noreferrer">
                                                                    {`${product.mfrInv}${product.mfrInvResults ? ':' : ''}`}
                                                                </a>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className={`${product.mfrInvResults ? 'results' : ''} `}>
                                                        {product.mfrInvResults}
                                                    </div>
                                                </div>
                                                <TipContainer className="ec3">
                                                    <ProgramTipsController
                                                        materialProgram={product.mfrInv}
                                                    />
                                                </TipContainer>
                                            </ProgramRow>
                                        )}
                                    </MaterialProgramBlock>
                                )}
                                {getLowEmittingInfo(product) && (
                                    <ProgramRow
                                        style={{ width: '100%' }}>
                                        <div className="program-spacing">
                                            <div className="program low-emitting">
                                                {getLowEmittingInfo(product)}
                                            </div>
                                        </div>
                                        <TipContainer className="ec3">
                                            <ProgramTipsController
                                                materialProgram={'LowEmitting'}
                                            />
                                        </TipContainer>
                                    </ProgramRow>
                                )}

                            </QuintileInformation>
                        )}
                        <ProductItemLabel weight={400} size={9} height={11} color={grayDark05}>
                            DESCRIPTION
                        </ProductItemLabel>
                        <DescriptionInputContainer>
                            <Tooltip title={product.description}>
                                <Input
                                    disabled={!editable}
                                    value={product.description}
                                    placeholder="Ex: SKU, color, use, etc."
                                    onChange={handleDescription}
                                    onBlur={handleDescriptionBlur}
                                    width={'100%'}
                                    height={28}
                                />
                            </Tooltip>
                        </DescriptionInputContainer>

                        <DiscussAndUploadAndCounts>
                            <PrimaryButton htmlType="submit" onClick={goToMFRPageDefault} width={105} height={24} style={{ marginTop: 16 }}>
                                <Text weight={400} size={12} height={11} style={{ marginTop: 2 }}>
                                    Discuss & upload
                                </Text>
                            </PrimaryButton>
                            <TextCounts weight={400} style={{ marginTop: 23 }}>
                                {product.commentsCount > 0 && (
                                    <TextCountsItem onClick={goToMFRPageComments} style={{ marginLeft: 0 }}>
                                        {product.commentsCount + ' comment' + (product.commentsCount > 1 ? `s` : ``)}
                                    </TextCountsItem>
                                )}
                                {product.documentsCount > 0 && (
                                    <>
                                        <TextCountsItem onClick={goToMFRPageDocuments}>
                                            {product.documentsCount + ' document' + (product.documentsCount > 1 ? `s` : ``)}
                                        </TextCountsItem>
                                        <TipContainerImpactAreas className="ec3">&nbsp;&nbsp;&nbsp;&nbsp;
                                            <ProgramTipsController
                                                materialProgram='dtImpactAreas'
                                                dataInput={product.documentTypes}
                                            />
                                        </TipContainerImpactAreas>
                                    </>
                                )}
                                {product.imagesCount > 0 && (
                                    <TextCountsItem onClick={goToMFRPageImages}>
                                        {product.imagesCount + ` image` + (product.imagesCount > 1 ? `s` : ``)}
                                    </TextCountsItem>
                                )}

                            </TextCounts>
                        </DiscussAndUploadAndCounts>

                    </ProductNameSection>
                    <SectionDivider>
                        <RowFlex>
                            <ProductItemPropertiesLeftSection className={`product-item`}>
                                <ProductItemLabel weight={400} size={9} height={11} color={grayDark05}>
                                    BASIS OF DESIGN
                                </ProductItemLabel>
                                <Select
                                    disabled={!editable}
                                    aria-required
                                    onChange={handleBasisOfDesign}
                                    value={state?.current?.basisOfDesignId}
                                    suffixIcon={
                                        <Image
                                            src="src/assets/images/ic-dropdown-arrow.svg"
                                            preview={false}
                                        />
                                    }
                                >
                                    {references?.allBasisOfDesignType
                                        ? references.allBasisOfDesignType.map((item) => (
                                            <Option style={{ fontSize: 12 }} value={item.id} key={item.id}>
                                                {item.name}
                                            </Option>
                                        ))
                                        : null}
                                </Select>

                                <ProductItemLabel weight={400} size={9} height={11} color={grayDark05}>
                                    SUBSTITUTIONS
                                </ProductItemLabel>
                                <Select
                                    disabled={!editable}
                                    aria-required
                                    value={state.current.substitutionTypeId}
                                    onChange={handleSubstitution}
                                    suffixIcon={
                                        <Image
                                            src="src/assets/images/ic-dropdown-arrow.svg"
                                            preview={false}
                                        />
                                    }
                                >
                                    {references?.allSubstitutionTypes
                                        ? references.allSubstitutionTypes.map((item) => (
                                            <Option style={{ fontSize: 12 }} value={item.id} key={item.id}>
                                                {item.name}
                                            </Option>
                                        ))
                                        : null}
                                </Select>

                            </ProductItemPropertiesLeftSection>

                            <ProductItemPropertiesRightSection className={`product-item`}>

                                <ProductItemLabel weight={400} size={9} height={11} color={grayDark05}>
                                    FINISH SCHEDULE ID
                                </ProductItemLabel>
                                <FinishScheduleIdInputContainer>
                                    <Input
                                        disabled={!editable}
                                        placeholder=""
                                        value={product.finishScheduleId}
                                        onChange={handleFinishScheduleId}
                                        onBlur={saveFinishScheduleId}
                                    />
                                    {errors.finishScheduleId && <FinishScheduleIdError>max size 50 characters</FinishScheduleIdError>}
                                </FinishScheduleIdInputContainer>

                                <ProductItemLabel weight={400} size={9} height={11} color={grayDark05}>
                                    PRICING
                                </ProductItemLabel>
                                <PricingInputContainer>
                                    <Input
                                        disabled={!editable}
                                        placeholder="ex: $5-9/sq. ft."
                                        value={product.pricing}
                                        onChange={handlePricing}
                                        onBlur={savePricing}
                                    />
                                    {errors.pricing && <PricingError>max size 75 characters</PricingError>}
                                </PricingInputContainer>
                                <ProductRadioButtonContainer>
                                    <Radio.Group
                                        value={state?.current?.pricingTypeId}
                                        onChange={handlePricingType}
                                        disabled={!editable}
                                    >
                                        {references?.allPricingType.map((pricingType) => (
                                            <Radio value={pricingType.id} key={pricingType.id}>
                                                {pricingType.name}
                                            </Radio>
                                        ))}
                                    </Radio.Group>
                                </ProductRadioButtonContainer>

                            </ProductItemPropertiesRightSection>

                            <DateSection className={`product-item`}>

                                <ProductItemLabel weight={400} size={9} height={11} color={grayDark05}>
                                    DATE ADDED
                                </ProductItemLabel>
                                <Text weight={400} size={12} height={14} color={blueDark01}>
                                    {toShortLocalDate(product.dateAdded)}
                                </Text>
                                <ProductItemDivider />
                                <ProductItemLabel weight={400} size={9} height={11} color={grayDark05} style={{ marginTop: 13.5 + `px` }}>
                                    LAST UPDATED
                                </ProductItemLabel>
                                <Text weight={400} size={12} height={14} color={blueDark01}>
                                    {toShortLocalDate(product.lastUpdated)}
                                </Text>

                            </DateSection>

                            <ActionsSection
                                className={`product-item ${lastProduct ? 'withoutBorder' : ''}`}
                            >
                                <ActionContainer>
                                    <ActionItem>
                                        {product.isMyFavorite ? (
                                            <Image
                                                src="src/assets/images/star.svg"
                                                preview={false}
                                                width={16}
                                                className={
                                                    getAccountSubscription() === SubscriptionTypes.FREE
                                                        || getAccountRole() === AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                        ? `${disabledImgClassName}` : ''
                                                }
                                                style={
                                                    {
                                                        cursor: getAccountSubscription() === SubscriptionTypes.FREE
                                                            || getAccountRole() === AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                            ? notAllowedClassName : 'pointer'
                                                    }
                                                }
                                                onClick={getAccountRole() !== AccountPermissionTypes.ACCOUNT_ASSOCIATE && handleMyFavorite}
                                            />
                                        ) : (
                                            <Image
                                                src="src/assets/images/star-empty.svg"
                                                preview={false}
                                                width={16}
                                                className={
                                                    getAccountSubscription() === SubscriptionTypes.FREE
                                                        || getAccountRole() === AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                        ? `${disabledImgClassName}` : ''
                                                }
                                                style={
                                                    {
                                                        cursor: getAccountSubscription() === SubscriptionTypes.FREE
                                                            || getAccountRole() === AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                            ? notAllowedClassName : 'pointer'
                                                    }
                                                }
                                                onClick={getAccountRole() !== AccountPermissionTypes.ACCOUNT_ASSOCIATE && handleMyFavorite}
                                            />
                                        )}
                                        <FavoriteButton onClick={getAccountRole() !== AccountPermissionTypes.ACCOUNT_ASSOCIATE && handleMyFavorite} className="favoriteButtonText"
                                            style={{ cursor: getAccountSubscription() === SubscriptionTypes.FREE || getAccountRole() === AccountPermissionTypes.ACCOUNT_ASSOCIATE ? notAllowedClassName : 'pointer' }}
                                        >
                                            <Text
                                                weight={400}
                                                size={12}
                                                height={14}
                                                color={
                                                    getAccountSubscription() === SubscriptionTypes.FREE
                                                        || getAccountRole() === AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                        ? grayDark03 : blueLink
                                                }>
                                                My Library
                                            </Text>
                                        </FavoriteButton>
                                    </ActionItem>
                                    {(getAccountSubscription() === SubscriptionTypes.OFFICE ||
                                        getAccountSubscription() ===
                                        SubscriptionTypes.FREE_TRIAL_OFFICE) && (
                                            <ActionItem>
                                                {product.isOfficeFavorite ? (
                                                    <ActiveOfficeFavoriteIcon
                                                        handleOfficeFavorite={getAccountRole() !== AccountPermissionTypes.ACCOUNT_ASSOCIATE && handleOfficeFavorite}
                                                    />
                                                ) : (
                                                    <>
                                                        <Image
                                                            className={canAddToOrgFavorites() ? '' : `${disabledImgClassName}`}
                                                            src="src/assets/images/star-empty.svg"
                                                            preview={false}
                                                            width={16}
                                                            style={{
                                                                cursor: getAccountSubscription() === SubscriptionTypes.FREE
                                                                    || getAccountRole() === AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                                    || !canAddToOrgFavorites()
                                                                    ? notAllowedClassName : 'pointer'
                                                            }}
                                                            onClick={getAccountRole() !== AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                                && canAddToOrgFavorites()
                                                                ? handleOfficeFavorite : undefined}
                                                        />
                                                        <FavoriteButton onClick={getAccountRole() !== AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                            && canAddToOrgFavorites()
                                                            ? handleOfficeFavorite : undefined}
                                                            className="favoriteButtonText"
                                                            style={{
                                                                cursor: getAccountSubscription() === SubscriptionTypes.FREE
                                                                    || getAccountRole() === AccountPermissionTypes.ACCOUNT_ASSOCIATE
                                                                    || !canAddToOrgFavorites()
                                                                    ? notAllowedClassName : 'pointer'
                                                            }}
                                                        >
                                                            <Text
                                                                weight={400}
                                                                size={12}
                                                                height={14}
                                                                color={canAddToOrgFavorites() ? blueLink : grayDark03}
                                                            >
                                                                Office Library
                                                            </Text>
                                                        </FavoriteButton>
                                                    </>
                                                )}
                                            </ActionItem>
                                        )}
                                    <ActionItem>
                                        <Checkbox
                                            name={'installed'}
                                            checked={product.isInstalled}
                                            onChange={handleInstalledProduct}
                                            disabled={!editable}
                                        />
                                        <Text weight={400} size={12} height={14} color={blueLink}>
                                            Installed
                                        </Text>
                                    </ActionItem>
                                </ActionContainer>
                                {editable && (
                                    <ActionCloseContainer>
                                        <ProductRowActions
                                            handleActions={handleActions}
                                            project={projectInfo}
                                            product={product}
                                        />
                                    </ActionCloseContainer>
                                )}
                            </ActionsSection>
                        </RowFlex>
                        <TagsList>
                            {product.liveGuideline && (
                                <LiveGuidelineTag
                                    to={`/live-guidelines/${product.liveGuideline.id}`}
                                    title={product.liveGuideline.name}
                                >
                                    <LiveGuidelineTagIcon src="src/assets/images/lightning-charge-fill.svg" alt="" />
                                    {truncateLiveGuidelineTagName(product.liveGuideline.name)}
                                </LiveGuidelineTag>
                            )}
                            {projectProductTags?.map((t: ProjectProductTag) => (
                                <Tag
                                    key={t.id}
                                    closable={canManageTag}
                                    onClose={() => deleteProjectProductTag(t.id)}
                                    color={t?.tag?.color?.display}
                                    borderColor={t?.tag?.color?.baseColor}
                                >
                                    {t?.tag?.name}
                                </Tag>
                            ))}
                            {canManageTag && <PrimarySmallButton
                                height={24}
                                style={{}}
                                onClick={openAddProjectProductTag}>
                                + Add tag
                            </PrimarySmallButton>}
                            {!canManageTag && !product.liveGuideline && projectProductTags?.length === 0 && (
                                <NoTagsText>
                                    Tags
                                </NoTagsText>
                            )}
                        </TagsList>
                    </SectionDivider>
                    <DeleteModal
                        isOpen={isOpen}
                        closeModal={onClose}
                        icon="building-circle-xmark.svg"
                        title="product"
                        text={`${product.productName}? ${isManuallyAdded ? 'Deleting it will delete it from all Projects and Libraries.' : ''}`}
                        reload={deleteProduct}
                        questionMark={false}
                    />
                    {isHelpOpen && (
                        <HelpModal
                            isOpen={isHelpOpen}
                            handleCancel={closeHelp}
                            project={projectInfo}
                            companyId={product.companyID}
                        />
                    )}
                </DetailItemsSection>
                <OfficePromptModal
                    isOpen={promptModal.isOpen}
                    closeModal={promptModal.onClose}
                    productId={product.officeFavoriteId}
                />
                <MyPromptModal
                    isOpen={myPromptModal.isOpen}
                    closeModal={myPromptModal.onClose}
                    productId={product.myFavoriteId}
                />
            </ProductDetailsWrapper>
        </ProductContainer>
    );
};

// Memoized: with ~850 rows mounted at once, skipping re-render of unchanged
// products (when an unrelated product or parent state changes) is a major win.
// Relies on stable callback props (updateDivisions/handleDeleteProduct are
// wrapped in useEventCallback) and immutable division updates so a changed
// product gets a new reference while siblings keep theirs.
export default React.memo(ProductItem);
